import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from './firebase';
import { logger } from '../utils/logger';
import type {
  DiagnoseRequest,
  DiagnoseResponse,
  ExtendPhotoRetentionRequest,
  ExtendPhotoRetentionResponse,
} from '../types/diagnosis';
import type {
  IdentifyRequest,
  IdentifyResponse,
  CareInfoRequest,
  CareInfoResponse,
  ReferencePhotoRequest,
  ReferencePhotoResponse,
} from '../types/api';
import type { ChatRequest, ChatResponse } from '../types/chat';
import { buildChatPayload } from './chatPayload';
import type { ClimateZone, Hemisphere } from '../types/user';

/**
 * Custom error class for rate limit errors
 * Includes the reset time in milliseconds for countdown display
 */
export class RateLimitError extends Error {
  public readonly resetInMs: number;

  constructor(message: string, resetInMs: number) {
    super(message);
    this.name = 'RateLimitError';
    this.resetInMs = resetInMs;
  }
}

/**
 * Check if an error is a rate limit error
 */
export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}

/**
 * Diagnose a plant problem using AI
 *
 * @param request - Plant ID, base64 image, and plant context
 * @returns Diagnosis result with problems, treatments, and product recommendations
 * @throws Error with user-friendly message on failure
 */
export async function diagnoseProblem(request: DiagnoseRequest): Promise<DiagnoseResponse> {
  const callable = httpsCallable<DiagnoseRequest, DiagnoseResponse>(functions, 'diagnoseProblem');

  logger.info('Diagnosis', 'Starting plant diagnosis', {
    plantId: request.plantId,
    hasImage: !!request.imageBase64,
    imageSize: request.imageBase64?.length ?? 0,
    plantContext: request.plantContext,
  });

  try {
    logger.debug('Diagnosis', 'Calling Cloud Function: diagnoseProblem');
    const result: HttpsCallableResult<DiagnoseResponse> = await callable(request);
    logger.info('Diagnosis', 'Diagnosis completed successfully', {
      isHealthy: result.data?.diagnosis?.isHealthy,
      problemCount: result.data?.diagnosis?.problems?.length ?? 0,
    });
    return result.data;
  } catch (error: unknown) {
    logger.error('Diagnosis', 'Cloud Function call failed', error, {
      plantId: request.plantId,
      rawError: error,
    });

    // Handle Firebase callable errors
    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string; details?: { retryAfterMinutes?: number } };

      logger.error('Diagnosis', `Firebase error: ${fbError.code}`, error, {
        code: fbError.code,
        message: fbError.message,
        details: fbError.details,
      });

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to diagnose plants.');
        case 'functions/not-found':
          throw new Error('Plant not found. It may have been deleted.');
        case 'functions/failed-precondition':
          throw new Error('Only identified plants can be diagnosed. Custom plants are not supported.');
        case 'functions/resource-exhausted': {
          const minutes = fbError.details?.retryAfterMinutes ?? 60;
          throw new Error(`Too many diagnoses. Please try again in ${minutes} minutes.`);
        }
        case 'functions/invalid-argument':
          throw new Error('Invalid request. Please try again with a valid photo.');
        default:
          throw new Error(`Failed to diagnose plant. Please try again. (${fbError.code})`);
      }
    }

    throw new Error('Failed to diagnose plant. Please try again.');
  }
}

/**
 * Extend the retention period of a diagnosis photo by 90 days
 *
 * @param request - Plant ID and diagnosis ID
 * @returns New expiration date
 * @throws Error with user-friendly message on failure
 */
export async function extendPhotoRetention(
  request: ExtendPhotoRetentionRequest
): Promise<ExtendPhotoRetentionResponse> {
  const callable = httpsCallable<ExtendPhotoRetentionRequest, ExtendPhotoRetentionResponse>(
    functions,
    'extendPhotoRetention'
  );

  try {
    const result: HttpsCallableResult<ExtendPhotoRetentionResponse> = await callable(request);
    return result.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string };

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to extend photo retention.');
        case 'functions/not-found':
          throw new Error('Diagnosis not found. It may have been deleted.');
        case 'functions/failed-precondition':
          throw new Error('Photo has already been deleted and cannot be extended.');
        default:
          throw new Error('Failed to extend photo retention. Please try again.');
      }
    }

    throw new Error('Failed to extend photo retention. Please try again.');
  }
}

/**
 * Identify a plant using AI
 *
 * @param request - Image URL or plant name for identification
 * @returns Identification result with common name, scientific name, and confidence
 * @throws Error with user-friendly message on failure
 */
export async function identifyPlant(request: IdentifyRequest): Promise<IdentifyResponse> {
  const callable = httpsCallable<IdentifyRequest, IdentifyResponse>(functions, 'identifyPlant');

  logger.info('Identify', 'Starting plant identification', {
    hasImageUrl: !!request.imageUrl,
    hasPlantName: !!request.plantName,
  });

  try {
    logger.debug('Identify', 'Calling Cloud Function: identifyPlant');
    const result: HttpsCallableResult<IdentifyResponse> = await callable(request);
    logger.info('Identify', 'Identification completed successfully', {
      commonName: result.data?.identification?.commonName,
      scientificName: result.data?.identification?.scientificName,
      confidence: result.data?.identification?.confidence,
    });
    return result.data;
  } catch (error: unknown) {
    logger.error('Identify', 'Cloud Function call failed', error);

    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string; details?: { retryAfterMinutes?: number } };

      logger.error('Identify', `Firebase error: ${fbError.code}`, error, {
        code: fbError.code,
        message: fbError.message,
        details: fbError.details,
      });

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to identify plants.');
        case 'functions/resource-exhausted': {
          const minutes = fbError.details?.retryAfterMinutes ?? 60;
          const resetInMs = minutes * 60 * 1000;
          throw new RateLimitError(
            `Too many identifications. Please try again in ${minutes} minutes.`,
            resetInMs
          );
        }
        case 'functions/invalid-argument':
          throw new Error('Invalid request. Please try again with a valid photo.');
        default:
          throw new Error(`Failed to identify plant. Please try again. (${fbError.code})`);
      }
    }

    throw new Error('Failed to identify plant. Please try again.');
  }
}

/**
 * Generate care information for a plant
 *
 * @param request - Scientific and common name of the plant
 * @returns Care information including light, water, humidity, etc.
 * @throws Error with user-friendly message on failure
 */
export async function generateCareInfo(request: CareInfoRequest): Promise<CareInfoResponse> {
  const callable = httpsCallable<CareInfoRequest, CareInfoResponse>(functions, 'generateCareInfo');

  try {
    const result: HttpsCallableResult<CareInfoResponse> = await callable(request);
    return result.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string; details?: { retryAfterMinutes?: number } };

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to get care information.');
        case 'functions/resource-exhausted': {
          const minutes = fbError.details?.retryAfterMinutes ?? 60;
          throw new Error(`Too many requests. Please try again in ${minutes} minutes.`);
        }
        case 'functions/invalid-argument':
          throw new Error('Invalid plant name. Please try again.');
        default:
          throw new Error('Failed to get care information. Please try again.');
      }
    }

    throw new Error('Failed to get care information. Please try again.');
  }
}

/**
 * Fetch a reference photo for a plant
 *
 * @param request - Scientific and common name of the plant
 * @returns Photo URL and attribution information
 * @throws Error with user-friendly message on failure
 */
export async function fetchReferencePhoto(request: ReferencePhotoRequest): Promise<ReferencePhotoResponse> {
  const callable = httpsCallable<ReferencePhotoRequest, ReferencePhotoResponse>(
    functions,
    'fetchReferencePhoto'
  );

  try {
    const result: HttpsCallableResult<ReferencePhotoResponse> = await callable(request);
    return result.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string; details?: { retryAfterMinutes?: number } };

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to fetch reference photos.');
        case 'functions/resource-exhausted': {
          const minutes = fbError.details?.retryAfterMinutes ?? 60;
          throw new Error(`Too many requests. Please try again in ${minutes} minutes.`);
        }
        case 'functions/not-found':
          throw new Error('No reference photo found for this plant.');
        default:
          throw new Error('Failed to fetch reference photo. Please try again.');
      }
    }

    throw new Error('Failed to fetch reference photo. Please try again.');
  }
}

/**
 * Chat with a plant expert AI about a specific plant
 *
 * @param request - Plant context and conversation messages
 * @returns AI reply text
 * @throws Error with user-friendly message on failure
 */
export async function chatWithPlant(request: ChatRequest): Promise<ChatResponse> {
  const callable = httpsCallable<ChatRequest, ChatResponse>(functions, 'chatWithPlant');

  logger.debug('Chat', 'Calling Cloud Function: chatWithPlant', {
    plant: request.plantContext.commonName,
    messageCount: request.messages.length,
  });

  try {
    const result: HttpsCallableResult<ChatResponse> = await callable(buildChatPayload(request));
    logger.info('Chat', 'Chat response received');
    return result.data;
  } catch (error: unknown) {
    logger.error('Chat', 'Cloud Function call failed', error);

    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string; details?: { resetIn?: number } };

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to chat.');
        case 'functions/resource-exhausted': {
          const resetInMs = fbError.details?.resetIn ?? 60 * 60000;
          const minutes = Math.ceil(resetInMs / 60000);
          throw new RateLimitError(
            `Too many messages. Please try again in ${minutes} minutes.`,
            resetInMs,
          );
        }
        case 'functions/invalid-argument':
          throw new Error('Invalid message. Please try again.');
        default:
          throw new Error('Failed to get a response. Please try again.');
      }
    }

    throw new Error('Failed to get a response. Please try again.');
  }
}

/**
 * Resolve a city name to a climate zone and hemisphere
 *
 * @param city - City name (e.g., "Portland, OR")
 * @returns Climate zone and hemisphere
 * @throws Error with user-friendly message on failure
 */
export async function resolveClimateZone(city: string): Promise<{ climateZone: ClimateZone; hemisphere: Hemisphere }> {
  const callable = httpsCallable<{ city: string }, { climateZone: ClimateZone; hemisphere: Hemisphere }>(
    functions,
    'resolveClimateZone'
  );

  try {
    const result = await callable({ city });
    return result.data;
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'code' in error) {
      const fbError = error as { code: string; message: string };

      switch (fbError.code) {
        case 'functions/unauthenticated':
          throw new Error('Please sign in to set your city.');
        case 'functions/resource-exhausted':
          throw new Error('Too many requests. Please try again later.');
        case 'functions/invalid-argument':
          throw new Error('Please enter a valid city name.');
        default:
          throw new Error('Could not determine climate zone. Please try again.');
      }
    }

    throw new Error('Could not determine climate zone. Please try again.');
  }
}

/**
 * Retry a function with exponential backoff
 *
 * @param fn - The async function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param delayMs - Base delay in milliseconds (default: 1000)
 * @returns The result of the function
 * @throws The last error encountered after all retries are exhausted
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delayMs = 1000
): Promise<T> {
  let lastError: Error | undefined;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (isRateLimitError(error)) throw error;
      lastError = error instanceof Error ? error : new Error(String(error));
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs * (i + 1)));
      }
    }
  }
  throw lastError;
}
